import React, { memo, useCallback, useEffect, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import {
  useGradeLevelsChangesContext,
  useGradeLevelsContext,
} from "../../context";
import classes from "./ActionButtons.scss";
import { Button } from "@toddle-edu/ds-web";
import { CopyOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";
import { useStudentGradeLevelChanges, useUpdateMapping } from "../../hooks";
import { MODAL_NAMES } from "../../modules/constants";
import CopyToOtherStudentsModal from "../CopyToOtherStudentsModal";
import WarningModal from "../WarningModal";
import UnsavedChangesDialog from "../UnsavedChangesDialog";
import { useBulkCopyGradeLevels } from "../../hooks/useBulkCopyGradeLevels";
import { useTranscriptsPermissions } from "TranscriptsV3/context/TranscriptsPermissionContext";
import { canManageAcademicRecords } from "TranscriptsV3/modules/PermissionHelpers";
import { useDispatch } from "Hooks";
import { setToastMsg } from "Login/modules/LoginModule";
import { useUnsavedChanges } from "TranscriptsV3/hooks";

const TEST_ID_PREFIX = "transcripts-studentDetails-configureGradeLevels";

const ActionButtons = props => {
  const { currentStudent, studentId, router, route } = props;
  const {
    id,
    name,
    grade: {
      curriculumProgram: { label } = {},
      globalGrade: { name: globalGradeName } = {},
    } = {},
  } = _.get(currentStudent, "yearGroup") || {};

  const { t } = useTranslation(["common", "transcript"]);
  const dispatch = useDispatch();

  const { isEditMode, setIsEditMode, setLoading } = useGradeLevelsContext();
  const { resetAllChanges } = useGradeLevelsChangesContext();

  const [modalDetails, setModalDetails] = useState({
    [MODAL_NAMES.COPY_TO_OTHER_STUDENTS]: { isOpen: false, data: {} },
    [MODAL_NAMES.CONFIGURATION_WARNING]: { isOpen: false, data: {} },
  });

  const { updated, removed, added } = useStudentGradeLevelChanges(studentId);

  const hasUnsavedChanges =
    updated.size > 0 || removed.size > 0 || added.size > 0;

  const { isBlocked, proceed, reset } = useUnsavedChanges(
    hasUnsavedChanges,
    router,
    route
  );

  const [showDiscardChanges, setShowDiscardChanges] = useState(false);

  useEffect(() => {
    if (isBlocked) {
      setShowDiscardChanges(true);
      reset();
    }
  }, [isBlocked, reset]);

  const { bulkCopy, loading: isCopying } = useBulkCopyGradeLevels();

  const updateMapping = useUpdateMapping();

  const permissionMap = useTranscriptsPermissions();

  const schoolId = _.get(
    currentStudent,
    "yearGroup.grade.curriculumProgram.id"
  );

  const canEdit = canManageAcademicRecords({
    permissionMap,
    schoolId,
  });

  const toggleMode = useCallback(() => {
    setIsEditMode(prev => !prev);
  }, [setIsEditMode]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) setShowDiscardChanges(true);
    else {
      resetAllChanges();
      setIsEditMode(false);
    }
  }, [hasUnsavedChanges, resetAllChanges, setIsEditMode]);

  const handleDiscard = useCallback(() => {
    setShowDiscardChanges(false);
    resetAllChanges();
    setIsEditMode(false);
    proceed();
  }, [resetAllChanges, setIsEditMode, proceed]);

  const handleSave = useCallback(async () => {
    const hasError = await updateMapping();

    if (hasError) return;
    dispatch(
      setToastMsg({
        type: "success",
        msg: "toastMsgs:entity_updated_successfully_plural",
        locale_params: [
          {
            key: "entity",
            value: "common:grade_level",
          },
        ],
      })
    );
    resetAllChanges();
    setIsEditMode(false);
  }, [dispatch, resetAllChanges, setIsEditMode, updateMapping]);

  const handleDialogSave = useCallback(async () => {
    setShowDiscardChanges(false);
    const hasError = await updateMapping();
    if (!hasError) {
      resetAllChanges();
      setIsEditMode(false);
      proceed();
    }
  }, [updateMapping, resetAllChanges, setIsEditMode, proceed]);

  const handleCloseDialog = useCallback(() => {
    setShowDiscardChanges(false);
  }, []);

  const handleOpenModal = useCallback(({ modalName, data = {} }) => {
    setModalDetails(prev => ({
      ...prev,
      [modalName]: {
        isOpen: true,
        data: data,
      },
    }));
  }, []);

  const handleCloseModal = useCallback(modalName => {
    setModalDetails(prev => ({
      ...prev,
      [modalName]: {
        isOpen: false,
        data: {},
      },
    }));
  }, []);

  const openCopyToOtherStudentsModal = useCallback(() => {
    handleOpenModal({ modalName: MODAL_NAMES.COPY_TO_OTHER_STUDENTS });
  }, [handleOpenModal]);

  const handleCopyApply = useCallback(
    async selectedStudentIds => {
      const { isSuccess, skipped, applied } = await bulkCopy({
        sourceStudentId: studentId,
        targetStudentIds: selectedStudentIds,
      });

      if (isSuccess)
        dispatch(
          setToastMsg({
            msg: "toastMsgs:successfully_with_label",
            type: "success",
            locale_params: [
              {
                key: "label",
                value: "toastMsgs:copied",
                isPlainText: false,
              },
            ],
            timeOut: 3000,
          })
        );
      else dispatch(setToastMsg("toastMsgs:something_went_wrong"));

      handleCloseModal(MODAL_NAMES.COPY_TO_OTHER_STUDENTS);
      if (!_.isEmpty(skipped)) {
        handleOpenModal({
          modalName: MODAL_NAMES.CONFIGURATION_WARNING,
          data: {
            skipped,
            applied,
          },
        });
      }
    },
    [bulkCopy, studentId, dispatch, handleCloseModal, handleOpenModal]
  );

  const handleCloseCopyToOtherStudentsModal = useCallback(() => {
    handleCloseModal(MODAL_NAMES.COPY_TO_OTHER_STUDENTS);
  }, [handleCloseModal]);

  const handleCloseConfigurationWarningModal = useCallback(() => {
    handleCloseModal(MODAL_NAMES.CONFIGURATION_WARNING);
  }, [handleCloseModal]);

  useEffect(() => {
    setLoading(isCopying);
  }, [isCopying, setLoading]);

  if (!canEdit) return null;

  return (
    <>
      <div className={classes.container}>
        {isEditMode ? (
          <>
            <Button
              type="outlined"
              variant="neutral"
              testId={`${TEST_ID_PREFIX}-cancel`}
              onClick={handleCancel}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="progressive"
              testId={`${TEST_ID_PREFIX}-save`}
              onClick={handleSave}
            >
              {t("common:save")}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="outlined"
              icon={<CopyOutlined />}
              variant="neutral"
              testId={`${TEST_ID_PREFIX}-copy`}
              onClick={openCopyToOtherStudentsModal}
            >
              {t("transcript:copy_to_other_students")}
            </Button>
            <Button
              icon={<PencilLineOutlined />}
              testId={`${TEST_ID_PREFIX}-edit`}
              onClick={toggleMode}
            >
              {t("common:edit")}
            </Button>
          </>
        )}
      </div>
      <UnsavedChangesDialog
        isOpen={showDiscardChanges}
        onDiscard={handleDiscard}
        onSave={handleDialogSave}
        onClose={handleCloseDialog}
      />
      {modalDetails[MODAL_NAMES.COPY_TO_OTHER_STUDENTS].isOpen && (
        <CopyToOtherStudentsModal
          studentId={studentId}
          yearGroupId={id}
          yearGroupName={name}
          globalGradeName={globalGradeName}
          handleCloseModal={handleCloseCopyToOtherStudentsModal}
          curriculumProgramsLabel={label}
          onApply={handleCopyApply}
          testId={`${TEST_ID_PREFIX}-copyToOtherStudentsModal`}
        />
      )}
      {modalDetails[MODAL_NAMES.CONFIGURATION_WARNING].isOpen && (
        <WarningModal
          data={modalDetails[MODAL_NAMES.CONFIGURATION_WARNING].data}
          onClose={handleCloseConfigurationWarningModal}
          testId={`${TEST_ID_PREFIX}-warningModal`}
        />
      )}
    </>
  );
};

export default memo(ActionButtons);
